<template>
  <v-dialog
    :value="value"
    :persistent="persistent || loading"
    :max-width="maxWidth"
    content-class="esign-confirm-dialog"
    @input="$emit('input', $event)"
  >
    <v-card class="esign-confirm">
      <div class="esign-confirm__body">
        <span class="esign-confirm__icon" :class="'esign-confirm__icon--' + tone" aria-hidden="true">
          <v-icon>{{ icon || defaultIcon }}</v-icon>
        </span>
        <div class="esign-confirm__content">
          <h3 class="esign-confirm__title">{{ title }}</h3>
          <div class="esign-confirm__text"><slot /></div>
          <div v-if="$slots.field" class="esign-confirm__field">
            <slot name="field" />
          </div>
        </div>
      </div>
      <div class="esign-confirm__actions">
        <v-btn text :disabled="cancelDisabled || loading" @click="$emit('input', false)">{{ cancelText }}</v-btn>
        <v-btn depressed :color="buttonColor" :loading="loading" @click="$emit('confirm')">{{ confirmText }}</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  // Confirmation dialog for actions that change a document (cancel, reject, deactivate, ...).
  // Usage: <esign-confirm-dialog v-model="open" title="..." confirm-text="..." tone="danger" @confirm="go">
  //          message <template #field>optional input</template>
  //        </esign-confirm-dialog>
  const TONES = {
    danger: { color: 'error', icon: 'mdi-alert-circle-outline' },
    warning: { color: 'warning', icon: 'mdi-alert-outline' },
    primary: { color: 'primary', icon: 'mdi-help-circle-outline' },
  }

  export default {
    name: 'EsignConfirmDialog',

    props: {
      value: { type: Boolean, default: false },
      title: { type: String, required: true },
      confirmText: { type: String, default: 'Ya' },
      cancelText: { type: String, default: 'Batal' },
      tone: { type: String, default: 'danger' },
      icon: { type: String, default: null },
      loading: { type: Boolean, default: false },
      cancelDisabled: { type: Boolean, default: false },
      persistent: { type: Boolean, default: false },
      maxWidth: { type: [String, Number], default: 520 },
    },

    computed: {
      buttonColor () { return (TONES[this.tone] || TONES.danger).color },
      defaultIcon () { return (TONES[this.tone] || TONES.danger).icon },
    },
  }
</script>

<style>
.esign-confirm__body {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 8px;
}

.esign-confirm__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
}

.esign-confirm__icon .v-icon {
  color: inherit !important;
  font-size: 24px;
}

.esign-confirm__icon--danger { color: var(--esign-error); background: var(--esign-error-bg); }
.esign-confirm__icon--warning { color: var(--esign-warning); background: var(--esign-warning-bg); }
.esign-confirm__icon--primary { color: var(--esign-primary-strong); background: var(--esign-primary-subtle); }

.esign-confirm__content {
  flex: 1 1 auto;
  min-width: 0;
}

/* style.css forces h3 sizes with !important */
.v-application .esign-confirm__title {
  margin: 8px 0 6px;
  font-size: 18px !important;
  font-weight: 700 !important;
  line-height: 1.35 !important;
  color: var(--esign-text);
}

.esign-confirm__text {
  font-size: 14px;
  line-height: 1.55;
  color: var(--esign-text-muted);
  overflow-wrap: anywhere;
}

.esign-confirm__text b,
.esign-confirm__text strong {
  color: var(--esign-text);
}

.esign-confirm__field {
  margin-top: 16px;
}

.esign-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px 20px;
}

@media (max-width: 600px) {
  .esign-confirm__body {
    flex-direction: column;
    gap: 8px;
    padding: 20px 20px 4px;
  }

  .esign-confirm__actions {
    flex-direction: column-reverse;
    padding: 16px 20px 20px;
  }

  .esign-confirm__actions .v-btn {
    width: 100%;
  }
}
</style>
